import { SelectChangeEvent } from '@mui/material';
import { useSelectors } from 'hooks/useSelectors';
import { useCallback, useEffect, useState } from 'react';

import {
  IOrder,
  IStatusNursery,
  ITypeFilters,
} from 'v2/resources/types/nurseries';

import {
  IResponseCyclesCode,
  ISpecieValue,
  IStatusCycles,
} from 'v2/resources/types/cycles';
import {
  useAppDispatch,
  useAppSelector,
} from 'v2/resources/hooks/useAppSelector';
import {
  disableDashboardError,
  disableDashboardLoading,
  disableNurserySelected,
  enableDashboardError,
  enableDashboardLoading,
  enableNurserySelected,
} from 'v2/store/dashboard/slice';
import { useGetNursery } from 'v2/resources/hooks/services/nursery/useNursery';
import { setNurseries } from 'v2/store/nurseries/slice';
import { useGetCycles } from 'v2/resources/hooks/services/cycles/useCycles';
import { setCycles } from 'v2/store/cycles/slice';
import {
  setDataChatCycles,
  setDataChatNurseries,
} from 'v2/store/dataChat/slice';
import { useWindowSizes } from 'v2/resources/hooks/useWindowSizes';

const useFilters = () => {
  const dispatch = useAppDispatch();
  const { nurserySelected } = useAppSelector((state) => state.dashboard);
  const { nameProperty } = useAppSelector((state) => state.nameProperty);
  const { cyclesCode } = useAppSelector((state) => state.cyclesCodeSlice);

  const { isMobile, isTablet } = useWindowSizes();

  const [statusNursery, setStatusNursery] = useState<IStatusNursery>('Todos');
  const [typeFilterNurseries, setTypeFilterNurseries] =
    useState<ITypeFilters>('Todos');
  const [typeFilterCycles, setTypeFilterCyles] =
    useState<ITypeFilters>('Todos');
  const [cyclesSelected, setCycleSelected] = useState<IResponseCyclesCode[]>();
  const [specie, setSpecie] = useState<ISpecieValue>('all');
  const [orderNurseries, setOrderNurseries] = useState<IOrder>('name');
  const [orderCycles, setOrderCycles] = useState<IOrder>('name');
  const [statusCycle, setStatusCycle] = useState<IStatusCycles>('Todos');

  const renderValueCycle =
    cyclesSelected?.map((cc) => `Ciclo ${cc.cod}`).join(',') ?? '';

  const handleNurseryActive = () => {
    dispatch(enableNurserySelected());
  };

  const handleNurseryDisable = () => {
    dispatch(disableNurserySelected());
  };

  const handleStatusNursery = (status: SelectChangeEvent<unknown>) => {
    setStatusNursery(status.target.value as IStatusNursery);
  };

  const handleTypeFilterNurseries = (type: SelectChangeEvent<unknown>) => {
    setTypeFilterNurseries(type.target.value as ITypeFilters);
  };
  const handleTypeFilterCycles = (type: SelectChangeEvent<unknown>) => {
    setTypeFilterCyles(type.target.value as ITypeFilters);
  };

  const handleCycle = (cycle: IResponseCyclesCode) => {
    setCycleSelected((prevState = []) => {
      const isSelected = prevState.some((item) => item.id === cycle.id);

      return isSelected
        ? prevState.filter((item) => item.id !== cycle.id)
        : [...prevState, cycle];
    });
  };

  const handleChangeSpecie = (status: SelectChangeEvent<unknown>) => {
    setSpecie(status.target.value as ISpecieValue);
  };

  const handleChangeOrderNurseries = (status: SelectChangeEvent<unknown>) => {
    setOrderNurseries(status.target.value as IOrder);
  };
  const handleChangeOrderCyles = (status: SelectChangeEvent<unknown>) => {
    setOrderCycles(status.target.value as IOrder);
  };

  const handleChangeStatusCycle = (status: SelectChangeEvent<unknown>) => {
    setStatusCycle(status.target.value as IStatusCycles);
  };

  const {
    data: nursery,
    isLoading: isLoadingNursery,
    isError: isErrorNursery,
  } = useGetNursery(
    {
      orderBy: orderNurseries,
      status: statusNursery,
      type: typeFilterNurseries,
    },
    nameProperty
  );

  const {
    data: cyclesLoaded,
    isLoading: isLoadingCycles,
    isError: isErrorCycle,
  } = useGetCycles(
    {
      specie,
      orderBy: orderCycles,
      type: typeFilterCycles,
      cycleCode: cyclesSelected?.map((c) => c.cod).join(',') ?? '',
      status: statusCycle,
    },
    nameProperty
  );

  const handleFilterNursery = useCallback(() => {
    if (nursery) {
      dispatch(setNurseries(nursery));
      dispatch(setDataChatNurseries(nursery));
      dispatch(disableDashboardError());
    }
  }, [nursery]);

  const handleErrorNursery = useCallback(() => {
    if (isErrorNursery) {
      dispatch(enableDashboardError());
    }
  }, [isErrorNursery]);

  const handleLoadingNursery = useCallback(() => {
    if (isLoadingNursery) {
      dispatch(enableDashboardLoading());
      return;
    }

    dispatch(disableDashboardLoading());
  }, [isLoadingNursery]);

  const handleFilterCycles = useCallback(() => {
    if (cyclesLoaded) {
      dispatch(setCycles(cyclesLoaded));
      dispatch(setDataChatCycles(cyclesLoaded));

      dispatch(disableDashboardError());
    }
  }, [cyclesLoaded]);

  const handleErrorCyles = useCallback(() => {
    if (isErrorCycle) {
      dispatch(enableDashboardError());
    }
  }, [isErrorCycle]);

  const handleLoadingCycles = useCallback(() => {
    if (isLoadingCycles) {
      dispatch(enableDashboardLoading());
      return;
    }

    dispatch(disableDashboardLoading());
  }, [isLoadingCycles]);

  useEffect(() => {
    handleFilterNursery();
    handleLoadingNursery();
    handleErrorNursery();
  }, [handleFilterNursery, handleLoadingNursery, handleErrorNursery]);

  useEffect(() => {
    handleFilterCycles();
    handleErrorCyles();
    handleLoadingCycles();
  }, [handleFilterCycles, handleErrorCyles, handleLoadingCycles]);

  return {
    nurserySelected,
    statusNursery,
    typeFilterNurseries,
    typeFilterCycles,
    cyclesCode,
    cyclesSelected,
    specie,
    renderValueCycle,
    orderNurseries,
    orderCycles,
    statusCycle,
    isMobile,
    isTablet,

    handleNurseryActive,
    handleNurseryDisable,
    handleStatusNursery,
    handleCycle,
    handleChangeSpecie,
    handleChangeStatusCycle,
    handleChangeOrderNurseries,
    handleChangeOrderCyles,
    handleTypeFilterNurseries,
    handleTypeFilterCycles,
  };
};
export default useFilters;
